import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Target, Megaphone, ExternalLink, Lightbulb, TrendingUp, Sparkles, HelpCircle, Loader2 } from 'lucide-react';
import api from '../services/api';
import EnginePill from '../components/EnginePill';
import AIChat from '../components/AIChat';

const WARMTH_LABELS = {
  hot: { label: 'Hot', tooltip: 'Cites competitors but not you — strongest pitch target', className: 'bg-red-100 text-red-800 border-red-200' },
  shared: { label: 'Shared', tooltip: 'Already cites both — relationship to deepen', className: 'bg-amber-100 text-amber-800 border-amber-200' },
  covered: { label: 'Covered', tooltip: 'Cites you, no competitors — already winning here', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  cold: { label: 'Cold', tooltip: 'No brand or competitor citations yet — exploratory', className: 'bg-slate-100 text-slate-700 border-slate-200' }
};

const Opportunities = () => {
  const navigate = useNavigate();
  const [websites, setWebsites] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [diagnoseOpen, setDiagnoseOpen] = useState(false);
  const [diagnoseGap, setDiagnoseGap] = useState(null);
  const [diagnoseLoading, setDiagnoseLoading] = useState(false);
  const [diagnoseData, setDiagnoseData] = useState(null);
  const [diagnoseError, setDiagnoseError] = useState('');
  const [minVolume, setMinVolume] = useState(0);

  useEffect(() => {
    const fetchWebsites = async () => {
      try {
        const response = await api.get('/websites');
        setWebsites(response.data.websites);
        if (response.data.websites?.length === 1) {
          setSelectedWebsite(response.data.websites[0].id);
        }
      } catch (err) {
        setError('Failed to fetch websites');
      }
    };
    fetchWebsites();
  }, []);

  useEffect(() => {
    if (!selectedWebsite) {
      setData(null);
      return;
    }
    const fetchOpportunities = async () => {
      setLoading(true);
      setError('');
      try {
        const qs = minVolume > 0 ? `?minVolume=${minVolume}` : '';
        const response = await api.get(`/opportunities/${selectedWebsite}${qs}`);
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch opportunities');
      } finally {
        setLoading(false);
      }
    };
    fetchOpportunities();
  }, [selectedWebsite, minVolume]);

  const handleDiagnose = async (gap) => {
    setDiagnoseGap(gap);
    setDiagnoseOpen(true);
    setDiagnoseLoading(true);
    setDiagnoseError('');
    setDiagnoseData(null);
    try {
      const resp = await api.post(`/opportunities/${selectedWebsite}/diagnose`, { keywordId: gap.keywordId });
      setDiagnoseData(resp.data);
    } catch (err) {
      setDiagnoseError(err.response?.data?.message || 'Failed to generate diagnosis');
    } finally {
      setDiagnoseLoading(false);
    }
  };

  const handleOptimiseGap = (gap) => {
    const targetUrl = gap.competitorUrls?.[0]?.url || '';
    const params = new URLSearchParams({
      websiteId: selectedWebsite,
      keywordId: gap.keywordId,
      ...(targetUrl ? { targetUrl } : {})
    });
    navigate(`/optimize?${params.toString()}`);
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold tracking-tight">Opportunities</h1>
            <EnginePill />
          </div>
          <p className="text-muted-foreground mt-2">
            Content gaps where competitors are cited and you're not, and media outlets worth pitching.
          </p>
          <p className="text-sm text-muted-foreground mt-2">These are untested content hypotheses. Use <a className="underline" href="/workflow">AI Workflow</a> to test a candidate, review the evidence and create an approved content plan.</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Select a website
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedWebsite} onValueChange={setSelectedWebsite}>
              <SelectTrigger className="max-w-md">
                <SelectValue placeholder="Pick a website to analyse" />
              </SelectTrigger>
              <SelectContent>
                {websites.map(w => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.brandName} ({w.domain})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading && (
          <Alert>
            <AlertDescription>Loading opportunities…</AlertDescription>
          </Alert>
        )}

        {data && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">Keywords tracked</p>
                  <p className="text-2xl font-bold">{data.summary.totalKeywords}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">Content gaps</p>
                  <p className="text-2xl font-bold text-orange-600">{data.summary.gapsFound}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">Media outlets seen</p>
                  <p className="text-2xl font-bold">{data.summary.mediaOutletsFound}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">Hot pitch targets</p>
                  <p className="text-2xl font-bold text-red-600">{data.summary.hotTargets}</p>
                </CardContent>
              </Card>
            </div>

            {/* Content Gaps */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5" />
                      Content Gaps
                    </CardTitle>
                    <CardDescription>
                      Keywords where ChatGPT cites competitors but has zero citations from your brand. Ranked by priority (competitor count × Google search volume) — biggest prizes first.
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <span className="text-muted-foreground mr-1">Min volume:</span>
                    {[
                      { value: 0, label: 'Any' },
                      { value: 100, label: '100+' },
                      { value: 1000, label: '1k+' },
                      { value: 5000, label: '5k+' },
                      { value: 10000, label: '10k+' }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setMinVolume(opt.value)}
                        className={`px-2 py-1 rounded transition-colors font-medium ${
                          minVolume === opt.value
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {data.contentGaps.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No clear content gaps — every tracked keyword has at least one brand citation, or none have competitor citations yet. Keep running keywords to surface gaps.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Keyword</TableHead>
                        <TableHead className="w-28 text-right">
                          <span title="Monthly Google organic search volume (Ahrefs). NOT how often it's asked of ChatGPT. Used to rank gaps by traffic potential.">
                            Volume<span className="text-[10px] block font-normal">Google, US</span>
                          </span>
                        </TableHead>
                        <TableHead className="w-28 text-right">Competitor citations</TableHead>
                        <TableHead>Top competitor URLs</TableHead>
                        <TableHead className="w-44 text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.contentGaps.map(gap => (
                        <TableRow key={gap.keywordId}>
                          <TableCell className="font-medium">{gap.keyword}</TableCell>
                          <TableCell className="text-right">
                            {gap.organicVolume === null || gap.organicVolume === undefined ? (
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : (
                              <span className={`text-sm font-medium ${gap.organicVolume === 0 ? 'text-muted-foreground' : ''}`}>
                                {gap.organicVolume.toLocaleString()}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="destructive">{gap.competitorCitations}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              {gap.competitorUrls.map((u, i) => (
                                <a
                                  key={i}
                                  href={u.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block text-xs text-muted-foreground hover:text-primary truncate max-w-md"
                                  title={u.title}
                                >
                                  {u.domain} — {u.title || u.url}
                                </a>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-2 justify-end flex-wrap">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDiagnose(gap)}
                              >
                                <Sparkles className="h-3 w-3 mr-1" />
                                Why?
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/keyword/${gap.keywordId}`)}
                              >
                                View
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleOptimiseGap(gap)}
                              >
                                <Lightbulb className="h-3 w-3 mr-1" />
                                Optimise
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Affiliate / PR Opportunities */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Megaphone className="h-5 w-5" />
                  Media & Affiliate Opportunities
                </CardTitle>
                <CardDescription>
                  Media outlets that ChatGPT cites for your tracked keywords. "Hot" targets already cite your competitors but not you — strongest case for outreach or affiliate placement.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {data.affiliateOpportunities.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No media-classified citations yet. Once keywords have run and the media classifier has tagged the citations, this list will populate.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Domain</TableHead>
                        <TableHead className="w-24 text-right">Citations</TableHead>
                        <TableHead className="w-32">Mix</TableHead>
                        <TableHead className="w-24 text-right">Keywords</TableHead>
                        <TableHead className="w-28">Warmth</TableHead>
                        <TableHead className="w-20 text-right">Visit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.affiliateOpportunities.map(opp => {
                        const warmth = WARMTH_LABELS[opp.warmth] || WARMTH_LABELS.cold;
                        return (
                          <TableRow key={opp.domain}>
                            <TableCell className="font-medium">{opp.domain}</TableCell>
                            <TableCell className="text-right">{opp.totalCitations}</TableCell>
                            <TableCell>
                              <div className="flex gap-1 text-xs">
                                {opp.brandCitations > 0 && (
                                  <span className="text-emerald-700">{opp.brandCitations}b</span>
                                )}
                                {opp.competitorCitations > 0 && (
                                  <span className="text-red-700">{opp.competitorCitations}c</span>
                                )}
                                {opp.otherCitations > 0 && (
                                  <span className="text-muted-foreground">{opp.otherCitations}o</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{opp.keywordCount}</TableCell>
                            <TableCell>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="outline" className={warmth.className}>
                                    {warmth.label}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>{warmth.tooltip}</TooltipContent>
                              </Tooltip>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                asChild
                              >
                                <a
                                  href={`https://${opp.domain}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={`Visit ${opp.domain}`}
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </>
        )}
        {selectedWebsite && <AIChat websiteId={selectedWebsite} />}

        <Dialog open={diagnoseOpen} onOpenChange={setDiagnoseOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Why didn't I rank: "{diagnoseGap?.keyword}"
              </DialogTitle>
              <DialogDescription>
                AI analysis of the top competitor pages ChatGPT cites for this keyword — what they have, what you'd need to match.
              </DialogDescription>
            </DialogHeader>
            {diagnoseLoading ? (
              <div className="py-8 flex flex-col items-center gap-3 text-sm text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <p>Reading {diagnoseGap?.competitorUrls?.length || 0} competitor pages…</p>
              </div>
            ) : diagnoseError ? (
              <Alert variant="destructive">
                <AlertDescription>{diagnoseError}</AlertDescription>
              </Alert>
            ) : diagnoseData?.diagnosis ? (
              <div className="space-y-5">
                <div className="rounded-lg bg-muted/50 p-4">
                  <p className="text-sm leading-relaxed font-medium">{diagnoseData.diagnosis.summary}</p>
                </div>
                {diagnoseData.diagnosis.whyTheyWin?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                      <TrendingUp className="h-4 w-4 text-emerald-600" />
                      Why those pages get cited
                    </h4>
                    <ul className="space-y-1.5 text-sm">
                      {diagnoseData.diagnosis.whyTheyWin.map((b, i) => (
                        <li key={i} className="flex gap-2"><span className="text-emerald-600 mt-0.5">▸</span><span>{b}</span></li>
                      ))}
                    </ul>
                  </div>
                )}
                {diagnoseData.diagnosis.yourGap?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                      <HelpCircle className="h-4 w-4 text-orange-600" />
                      What's missing on your side
                    </h4>
                    <ul className="space-y-1.5 text-sm">
                      {diagnoseData.diagnosis.yourGap.map((b, i) => (
                        <li key={i} className="flex gap-2"><span className="text-orange-600 mt-0.5">▸</span><span>{b}</span></li>
                      ))}
                    </ul>
                  </div>
                )}
                {diagnoseData.diagnosis.action && (
                  <div className="rounded-lg border-2 border-primary/30 bg-primary/[0.04] p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-1">Highest-priority next move</p>
                    <p className="text-sm leading-relaxed">{diagnoseData.diagnosis.action}</p>
                    <Button
                      size="sm"
                      className="mt-3"
                      onClick={() => { setDiagnoseOpen(false); handleOptimiseGap(diagnoseGap); }}
                    >
                      <Lightbulb className="h-3 w-3 mr-1" />
                      Generate brief for this keyword
                    </Button>
                  </div>
                )}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default Opportunities;
